import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { FrontendMandant, GlobalFrontendUser } from '@reflact/reflactportal';
import { ToastrService } from 'ngx-toastr';
import { MandantService } from 'src/app/shared/services/mandant.service';
import { SettingsService } from 'src/app/shared/services/settings.service';
import { TileService } from 'src/app/shared/services/tile.service';
import { UserService } from 'src/app/shared/services/user.service';
import { Tile } from 'src/app/shared/types/tile.types';

/** Mandant + Anzahl der ihm zugewiesenen Kacheln — Basis für die Top-Mandanten-Liste. */
interface MandantTileCount {
  mandant: FrontendMandant;
  tileCount: number;
}

/** Übersichts-Dashboard für reflact-Admins (`/admin/dashboard`) — aggregiert Kennzahlen aus
 * Mandanten-, Nutzer- und Kachelverwaltung sowie den Systemeinstellungen, damit der Ist-Zustand
 * des Portals auf einen Blick sichtbar ist, ohne jeden Admin-Bereich einzeln zu öffnen. */
@Component({
  templateUrl: './dashboard-administration.component.html',
  styleUrls: ['./dashboard-administration.component.scss'],
  standalone: false
})
export class DashboardAdministrationComponent implements OnInit {
  public loading = true;
  /** Zeigt einen Hinweis statt des Dashboards, falls eine der Ladeanfragen fehlschlägt — sonst
   * bliebe bei einem Fehler nur das Laderädchen unendlich stehen, siehe ngOnInit(). */
  public loadError = false;

  public mandants: FrontendMandant[] = [];
  public globalUsers: GlobalFrontendUser[] = [];
  public tiles: Tile[] = [];
  public openAiConfigured = false;

  constructor(
    private mandantService: MandantService,
    private userService: UserService,
    private tileService: TileService,
    private settingsService: SettingsService,
    private toastrService: ToastrService,
    private cdr: ChangeDetectorRef
  ) { }

  async ngOnInit(): Promise<void> {
    try {
      const [mandants, globalUsers, tiles, openAiConfigured] = await Promise.all([
        this.mandantService.getMandants(),
        this.userService.getGlobalUsers(),
        this.tileService.listTiles(),
        this.settingsService.getOpenAiStatus()
      ]);
      this.mandants = mandants;
      this.globalUsers = globalUsers;
      this.tiles = tiles;
      this.openAiConfigured = openAiConfigured;
    } catch (error) {
      console.error('Failed to load dashboard data', error);
      this.loadError = true;
      this.toastrService.error($localize`:@@dashboardAdminLoadError:Die Dashboard-Daten konnten nicht geladen werden. Bitte lade die Seite neu.`);
    } finally {
      this.loading = false;
      // async ngOnInit continuations don't reliably trigger a new CD cycle on their own here
      // (same issue as layout.component.ts/tile-administration.component.ts) - force one so the
      // dashboard actually shows up instead of the spinner staying forever.
      this.cdr.detectChanges();
    }
  }

  public get mandantCount(): number {
    return this.mandants.length;
  }

  public get userCount(): number {
    return this.globalUsers.length;
  }

  public get reflactAdminCount(): number {
    return this.globalUsers.filter(u => u.reflactadmin).length;
  }

  public get twoFactorEnabledCount(): number {
    return this.globalUsers.filter(u => u.enableAuthentificator).length;
  }

  public get twoFactorPercent(): number {
    if (this.userCount === 0) return 0;
    return Math.round((this.twoFactorEnabledCount / this.userCount) * 100);
  }

  public get tileCount(): number {
    return this.tiles.length;
  }

  public get applicationTileCount(): number {
    return this.tiles.filter(t => t.kind === 'application').length;
  }

  public get linkTileCount(): number {
    return this.tiles.filter(t => t.kind === 'link').length;
  }

  public get applicationTilePercent(): number {
    if (this.tileCount === 0) return 0;
    return Math.round((this.applicationTileCount / this.tileCount) * 100);
  }

  /** Durchschnittliche Anzahl an Kacheln, die einem Mandanten zugewiesen sind — grober Indikator
   * dafür, wie gut das Kachelangebot im Portal genutzt wird. */
  public get avgTilesPerMandant(): number {
    if (this.mandantCount === 0) return 0;
    const total = this.mandants.reduce((sum, m) => sum + (m.tiles?.length ?? 0), 0);
    return Math.round((total / this.mandantCount) * 10) / 10;
  }

  /** Mandanten ohne eine einzige zugewiesene Kachel — potenzielles Onboarding-Problem, daher als
   * Hinweis-Kachel im Dashboard hervorgehoben. */
  public get mandantsWithoutTiles(): FrontendMandant[] {
    return this.mandants.filter(m => (m.tiles?.length ?? 0) === 0);
  }

  /** Top 5 Mandanten nach Anzahl zugewiesener Kacheln, absteigend sortiert. */
  public get topMandantsByTiles(): MandantTileCount[] {
    return [...this.mandants]
      .map(m => ({ mandant: m, tileCount: m.tiles?.length ?? 0 }))
      .sort((a, b) => b.tileCount - a.tileCount)
      .slice(0, 5);
  }
}
